/**
 * Platform Reports Service
 *
 * API client for background report jobs.
 */
import { apiClient } from './api'

export interface ReportType {
  id: string
  name: string
  description: string
  category: string
  url_slug: string
  exception_code?: string
  is_exception_report?: boolean
  /** Business classification (process_issue / data_discrepancy) - set on the
   * synthetic per-code exception rows built by buildExceptionRows. */
  exception_type?: string | null
  exception_types?: Array<{
    key: string
    label: string
    description?: string
    data_domain?: string
    exception_type?: string | null
    search_keywords?: string[]
    visible_to_roles?: string[] | null
  }>
  visible_to_roles?: string[] | null
  searchHaystack?: string
}

export interface ReportTypesPayload {
  reportTypes: ReportType[]
  favourites: string[]
  searchAliases: Record<string, string[]>
}

export interface ReportFilters {
  fp_code?: string
  country?: string
  start_date?: string
  end_date?: string
  limit?: number
}

export interface PlatformReportJob {
  job_id: string
  report_type: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  filters?: ReportFilters
  progress_percentage: number
  progress_message?: string
  result_summary?: Record<string, any>
  result_data?: Array<Record<string, any>>
  result_row_count?: number
  created_at?: string
  completed_at?: string
  error_message?: string
}

export interface RecentJob {
  job_id: string
  created_at?: string
  completed_at?: string
  result_row_count?: number
  filters_summary: string
}

export const platformReportsService = {
  /**
   * List available report types for hub display.
   *
   * Returns both the report-type rows and the bidirectional alias
   * lookup so the hub's client-side search expands "invoice" to
   * "sale / receipt / transaction" the same way the inspector hub does.
   */
  listReportTypes: async (): Promise<ReportTypesPayload> => {
    const response = await apiClient.get<{
      report_types: ReportType[]
      favourites?: string[]
      search_aliases?: Record<string, string[]>
    }>('/admin/reports/types')
    return {
      reportTypes: response.data.report_types,
      favourites: response.data.favourites ?? [],
      searchAliases: response.data.search_aliases ?? {},
    }
  },

  /**
   * Add a report to the caller's favourites (idempotent). Returns the
   * updated favourite-id list.
   */
  addFavourite: async (id: string): Promise<string[]> => {
    const response = await apiClient.put<{ favourites: string[] }>(
      `/admin/reports/favourites/${id}`
    )
    return response.data.favourites
  },

  /**
   * Remove a report from the caller's favourites (idempotent). Returns the
   * updated favourite-id list.
   */
  removeFavourite: async (id: string): Promise<string[]> => {
    const response = await apiClient.delete<{ favourites: string[] }>(
      `/admin/reports/favourites/${id}`
    )
    return response.data.favourites
  },

  /**
   * Submit a new report job for background execution
   */
  submitJob: async (reportType: string, filters?: ReportFilters): Promise<{ job_id: string }> => {
    const response = await apiClient.post<{ job_id: string }>('/admin/reports/jobs', {
      report_type: reportType,
      filters,
    })
    return response.data
  },

  /**
   * Get job status and results (for polling)
   */
  getJobStatus: async (jobId: string): Promise<PlatformReportJob> => {
    const response = await apiClient.get<PlatformReportJob>(`/admin/reports/jobs/${jobId}`)
    return response.data
  },

  /**
   * List recent completed jobs for "Past Runs" dropdown
   */
  listRecentJobs: async (reportType: string, limit = 5): Promise<RecentJob[]> => {
    const response = await apiClient.get<{ jobs: RecentJob[] }>(
      `/admin/reports/jobs/recent/${reportType}`,
      { params: { limit } }
    )
    return response.data.jobs
  },

  /**
   * Download completed report as Excel file
   */
  downloadJob: async (
    jobId: string,
    reportName: string,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc'
  ): Promise<void> => {
    const params: Record<string, string> = {}
    if (sortBy) params.sort_by = sortBy
    if (sortDirection) params.sort_direction = sortDirection

    const response = await apiClient.get(`/admin/reports/jobs/${jobId}/download`, {
      responseType: 'blob',
      params,
    })

    // Prefer the server's Content-Disposition filename so report-type-specific
    // names (e.g. exceptions_<code>_YYYY-MM-DD_HHMM.xlsx) flow through.
    const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
    let filename: string | null = null
    if (typeof disposition === 'string') {
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
      if (match) filename = decodeURIComponent(match[1])
    }
    if (!filename) {
      const now = new Date()
      const datePart = now.toISOString().split('T')[0]
      const timePart = now.toTimeString().slice(0, 5).replace(':', '')
      filename = `${reportName}_${datePart}_${timePart}.xlsx`
    }

    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },
}
